import type { DragEvent } from 'react';
import { useRef } from 'react';

type DragAndDropState<TPayload> = {
  draggedPayload: TPayload | null;
  setDraggedPayload: (payload: TPayload | null) => void;
  hideDraggedElement?: boolean;
  onDragCapture?: () => void;
};

type DragPropsInput<TPayload> = {
  isDraggable: boolean;
  payload: TPayload;
};

type DropHandlersInput<TPayload> = {
  isDroppable: boolean;
  onDrop: (payload: TPayload) => void;
};

export const useDragAndDrop = <TPayload>({
  draggedPayload,
  setDraggedPayload,
  hideDraggedElement = true,
  onDragCapture,
}: DragAndDropState<TPayload>) => {
  const draggedElementRef = useRef<HTMLElement | null>(null);
  const draggedElementPreviousVisibilityRef = useRef<string>('');
  const hideDraggedElementRafRef = useRef<number | null>(null);

  const cleanupDragVisuals = () => {
    if (hideDraggedElementRafRef.current !== null) {
      cancelAnimationFrame(hideDraggedElementRafRef.current);
      hideDraggedElementRafRef.current = null;
    }

    if (hideDraggedElement && draggedElementRef.current) {
      draggedElementRef.current.style.visibility =
        draggedElementPreviousVisibilityRef.current;
      draggedElementRef.current = null;
      draggedElementPreviousVisibilityRef.current = '';
    }
  };

  const endDrag = () => {
    cleanupDragVisuals();
    setDraggedPayload(null);
  };

  const getDragProps = ({ isDraggable, payload }: DragPropsInput<TPayload>) => {
    if (!isDraggable) return undefined;

    const handleDragStart = (event: DragEvent<HTMLElement>) => {
      setDraggedPayload(payload);
      const draggedElement = event.currentTarget;
      draggedElementRef.current = draggedElement;
      draggedElementPreviousVisibilityRef.current =
        draggedElement.style.visibility;

      // Keep payload in the native drag data store so drop handlers
      // can reliably read it across drag targets/browsers.
      try {
        event.dataTransfer.setData('text/plain', JSON.stringify(payload));
      } catch {
        // ignore non-serializable payloads and rely on draggedPayload fallback
      }

      event.dataTransfer.effectAllowed = 'move';
      if (hideDraggedElement) {
        hideDraggedElementRafRef.current = requestAnimationFrame(() => {
          draggedElement.style.visibility = 'hidden';
          hideDraggedElementRafRef.current = null;
        });
      }

      if (onDragCapture) {
        hideDraggedElementRafRef.current = requestAnimationFrame(() => {
          onDragCapture();
          hideDraggedElementRafRef.current = null;
        });
      }
    };

    return {
      draggable: true,
      onDragStart: handleDragStart,
      onDragEnd: endDrag,
    };
  };

  const getDropHandlers = ({
    isDroppable,
    onDrop,
  }: DropHandlersInput<TPayload>) => {
    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
      if (!isDroppable) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      if (!isDroppable) return;
      event.preventDefault();
      const payload = event.dataTransfer.getData('text/plain');
      if (!payload) {
        if (draggedPayload !== null) {
          onDrop(draggedPayload);
        }
        return;
      }
      try {
        onDrop(JSON.parse(payload) as TPayload);
      } catch {
        if (draggedPayload !== null) {
          onDrop(draggedPayload);
        }
      }
    };

    return { isDroppable, handleDragOver, handleDrop };
  };

  return { draggedPayload, getDragProps, getDropHandlers, endDrag };
};
